'use client';

/**
 * FlowTime — настройки, проекты и работа с данными.
 *
 * Экспорт стоит рядом с удалением намеренно: единственная копия журнала
 * живёт в localStorage этого браузера, и очистка кэша стирает её без
 * предупреждения. Резервная копия должна попадаться на глаза раньше,
 * чем кнопка «удалить всё».
 */

import { useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Check, Download, Trash2, Upload } from 'lucide-react';
import type { FlowTimeApi } from '@/components/datahunter/use-flowtime';
import { FLOW_PALETTE, storageBytes, type FlowSettings } from '@/lib/flowtime-storage';
import { buildCsv, downloadFile } from '@/lib/flowtime-format';
import { playAlarm, previewAmbient, setVolume, unlockAudio } from '@/lib/flowtime-audio';

const PRESETS = [
  { id: 'classic', focusMin: 25, shortMin: 5, longMin: 15, longEvery: 4 },
  { id: 'deep', focusMin: 50, shortMin: 10, longMin: 30, longEvery: 2 },
  { id: 'ultradian', focusMin: 90, shortMin: 20, longMin: 30, longEvery: 2 },
  { id: 'sprint', focusMin: 15, shortMin: 3, longMin: 10, longEvery: 4 },
] as const;

const ALARMS: FlowSettings['alarm'][] = ['bell', 'chime', 'digital', 'wood', 'none'];
const AMBIENTS: FlowSettings['ambient'][] = ['none', 'tick', 'brown', 'rain'];

export function FlowSettingsPanel({
  flow,
  notify,
}: {
  flow: FlowTimeApi;
  notify: (message: string, tone?: 'ok' | 'error') => void;
}) {
  const t = useTranslations('dh_flowtime_ui');
  const s = flow.settings;
  const fileRef = useRef<HTMLInputElement>(null);
  const [projectName, setProjectName] = useState('');
  const [projectColor, setProjectColor] = useState(FLOW_PALETTE[1] ?? '#f59e0b');
  const [confirmWipe, setConfirmWipe] = useState(false);

  const stamp = new Date().toISOString().slice(0, 10);

  const exportJson = () => {
    const payload = { ...flow.data, session: null, exportedAt: Date.now(), app: 'flowtime' };
    downloadFile(`flowtime-${stamp}.json`, JSON.stringify(payload, null, 2), 'application/json');
    notify(t('data_saved'), 'ok');
  };

  const exportCsv = () => {
    if (flow.data.entries.length === 0) {
      notify(t('data_nothing'), 'error');
      return;
    }
    const csv = buildCsv(
      flow.data.entries,
      (id) => flow.taskById(id)?.title ?? '',
      (id) => flow.projectById(id)?.name ?? '',
    );
    downloadFile(`flowtime-${stamp}.csv`, csv, 'text/csv');
    notify(t('data_csv_saved'), 'ok');
  };

  const importFile = async (file: File, merge: boolean) => {
    try {
      const parsed: unknown = JSON.parse(await file.text());
      if (
        !parsed ||
        typeof parsed !== 'object' ||
        !Array.isArray((parsed as { entries?: unknown }).entries)
      ) {
        notify(t('data_bad_file'), 'error');
        return;
      }
      const added = flow.importData(parsed, merge);
      notify(merge ? t('data_imported', { count: added }) : t('data_replaced'), 'ok');
    } catch {
      notify(t('data_read_error'), 'error');
    }
  };

  const num = (key: keyof FlowSettings, min: number, max: number, step = 1) => (
    <input
      type="number"
      min={min}
      max={max}
      step={step}
      value={s[key] as number}
      onChange={(e) => {
        const raw = Number(e.target.value);
        if (!Number.isFinite(raw)) return;
        flow.updateSettings({ [key]: Math.min(max, Math.max(min, raw)) });
      }}
      className="w-20 px-3 py-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-sm text-center tabular-nums focus:outline-none focus:border-[var(--color-accent-500)]"
    />
  );

  const toggle = (key: keyof FlowSettings, label: string, hint?: string) => (
    <label className="flex items-center gap-3 py-2.5 border-b border-[var(--color-border)] last:border-0 cursor-pointer">
      <span className="flex-1">
        <span className="text-sm">{label}</span>
        {hint && <span className="block text-xs text-[var(--color-text-muted)]">{hint}</span>}
      </span>
      <input
        type="checkbox"
        checked={s[key] as boolean}
        onChange={(e) => {
          flow.updateSettings({ [key]: e.target.checked });
        }}
        className="w-4 h-4 accent-[var(--color-text)] cursor-pointer"
      />
    </label>
  );

  const bytes = storageBytes();
  const size =
    bytes < 1024
      ? `${String(bytes)} B`
      : bytes < 1_048_576
        ? `${(bytes / 1024).toFixed(1)} KB`
        : `${(bytes / 1_048_576).toFixed(2)} MB`;

  return (
    <div className="grid lg:grid-cols-2 gap-4 items-start">
      {/* Интервалы */}
      <Panel title={t('set_intervals')}>
        <Row label={t('set_focus')} unit={t('unit_min')}>
          {num('focusMin', 1, 180)}
        </Row>
        <Row label={t('set_short')} unit={t('unit_min')}>
          {num('shortMin', 1, 60)}
        </Row>
        <Row label={t('set_long')} unit={t('unit_min')}>
          {num('longMin', 1, 120)}
        </Row>
        <Row label={t('set_every')} unit={t('unit_pomodoros')}>
          {num('longEvery', 2, 12)}
        </Row>
        <Row label={t('set_goal')} unit={t('unit_hours')}>
          {num('goalHours', 0, 16, 0.5)}
        </Row>

        <div className="flex flex-wrap gap-2 pt-4">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => {
                flow.updateSettings({
                  focusMin: p.focusMin,
                  shortMin: p.shortMin,
                  longMin: p.longMin,
                  longEvery: p.longEvery,
                });
                notify(t('preset_applied', { name: t(`preset_${p.id}`) }), 'ok');
              }}
              className="px-3 py-1.5 rounded-full border border-[var(--color-border)] text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:border-[var(--color-text-muted)] transition-colors"
            >
              {t(`preset_${p.id}`)}
            </button>
          ))}
        </div>
      </Panel>

      {/* Поведение */}
      <Panel title={t('set_behavior')}>
        {toggle('autoStartBreak', t('set_auto_break'))}
        {toggle('autoStartFocus', t('set_auto_focus'))}
        {toggle('strict', t('set_strict'), t('set_strict_hint'))}
        {toggle('titleCountdown', t('set_title'))}
        {toggle('logBreaks', t('set_log_breaks'))}
        <label className="flex items-center gap-3 py-2.5 cursor-pointer">
          <span className="flex-1 text-sm">
            {t('set_notify')}
            <span className="block text-xs text-[var(--color-text-muted)]">
              {t('set_notify_hint')}
            </span>
          </span>
          <input
            type="checkbox"
            checked={s.notify}
            onChange={(e) => {
              if (!e.target.checked) {
                flow.updateSettings({ notify: false });
                return;
              }
              // Разрешение спрашивается только по явному действию: браузеры
              // блокируют запросы, сделанные при загрузке страницы.
              if (!('Notification' in window)) {
                notify(t('notify_unsupported'), 'error');
                return;
              }
              void Notification.requestPermission().then((res) => {
                flow.updateSettings({ notify: res === 'granted' });
                if (res !== 'granted') notify(t('notify_denied'), 'error');
              });
            }}
            className="w-4 h-4 accent-[var(--color-text)] cursor-pointer"
          />
        </label>
      </Panel>

      {/* Звук */}
      <Panel title={t('set_sound')}>
        <Row label={t('set_alarm')}>
          <select
            value={s.alarm}
            onChange={(e) => {
              const alarm = e.target.value as FlowSettings['alarm'];
              flow.updateSettings({ alarm });
              unlockAudio(s.volume);
              playAlarm(alarm, s.volume);
            }}
            className="px-3 py-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-sm text-[var(--color-text)] focus:outline-none focus:border-[var(--color-accent-500)]"
          >
            {ALARMS.map((a) => (
              <option key={a} value={a}>
                {t(`alarm_${a}`)}
              </option>
            ))}
          </select>
        </Row>

        <Row label={t('set_volume')} unit={`${String(Math.round(s.volume * 100))}%`}>
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={s.volume}
            onChange={(e) => {
              const volume = Number(e.target.value);
              flow.updateSettings({ volume });
              setVolume(volume);
            }}
            className="w-32 accent-[var(--color-text)]"
          />
        </Row>

        <Row label={t('set_ambient')}>
          <select
            value={s.ambient}
            onChange={(e) => {
              const ambient = e.target.value as FlowSettings['ambient'];
              flow.updateSettings({ ambient });
              unlockAudio(s.volume);
              // Короткая проба, чтобы выбор был осмысленным: во время
              // работы фон включится сам.
              if (ambient !== 'none' && !flow.running) previewAmbient(ambient, s.volume);
            }}
            className="px-3 py-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-sm text-[var(--color-text)] focus:outline-none focus:border-[var(--color-accent-500)]"
          >
            {AMBIENTS.map((a) => (
              <option key={a} value={a}>
                {t(`ambient_${a}`)}
              </option>
            ))}
          </select>
        </Row>

        <Row label={t('set_week_start')}>
          <select
            value={String(s.weekStart)}
            onChange={(e) => {
              flow.updateSettings({ weekStart: Number(e.target.value) === 0 ? 0 : 1 });
            }}
            className="px-3 py-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-sm text-[var(--color-text)] focus:outline-none focus:border-[var(--color-accent-500)]"
          >
            <option value="1">{t('week_monday')}</option>
            <option value="0">{t('week_sunday')}</option>
          </select>
        </Row>
      </Panel>

      {/* Проекты */}
      <Panel title={t('set_projects')}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (flow.addProject(projectName, projectColor)) setProjectName('');
          }}
          className="flex flex-wrap items-center gap-2 mb-3"
        >
          <input
            type="text"
            value={projectName}
            onChange={(e) => {
              setProjectName(e.target.value);
            }}
            placeholder={t('project_placeholder')}
            maxLength={60}
            className="flex-1 min-w-[140px] px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-sm placeholder:text-[var(--color-text-muted)] focus:outline-none focus:border-[var(--color-accent-500)]"
          />
          <div className="flex gap-1">
            {FLOW_PALETTE.slice(0, 6).map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => {
                  setProjectColor(c);
                }}
                aria-label={t('project_color', { color: c })}
                className={`w-6 h-6 rounded-full border-2 transition-transform ${
                  projectColor === c ? 'border-[var(--color-text)] scale-110' : 'border-transparent'
                }`}
                style={{ background: c }}
              />
            ))}
          </div>
          <button
            type="submit"
            className="dh-cta px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            {t('project_create')}
          </button>
        </form>

        {flow.data.projects.length === 0 ? (
          <p className="text-sm text-[var(--color-text-muted)]">{t('projects_empty')}</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {flow.data.projects.map((p) => {
              const tasks = flow.data.tasks.filter((x) => x.projectId === p.id && !x.done).length;
              return (
                <li
                  key={p.id}
                  className="group flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-[var(--color-bg-elevated)] transition-colors"
                >
                  <span
                    className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ background: p.color }}
                  />
                  <span className="flex-1 text-sm truncate">{p.name}</span>
                  <span className="text-xs text-[var(--color-text-muted)]">
                    {t('project_tasks', { count: tasks })}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      flow.deleteProject(p.id);
                      notify(t('project_deleted'));
                    }}
                    aria-label={t('project_delete', { name: p.name })}
                    className="w-6 h-6 grid place-items-center rounded text-[var(--color-text-muted)] opacity-0 group-hover:opacity-100 hover:text-[var(--color-danger-400)] transition-all"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </Panel>

      {/* Данные */}
      <Panel title={t('set_data')} className="lg:col-span-2">
        <p className="text-sm text-[var(--color-text-muted)] mb-4">
          {t('data_note', { entries: flow.data.entries.length, size })}
        </p>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={exportJson}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[var(--color-border)] text-sm hover:border-[var(--color-text-muted)] transition-colors"
          >
            <Download className="w-4 h-4" />
            {t('data_backup')}
          </button>
          <button
            type="button"
            onClick={exportCsv}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[var(--color-border)] text-sm hover:border-[var(--color-text-muted)] transition-colors"
          >
            <Download className="w-4 h-4" />
            {t('data_csv')}
          </button>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[var(--color-border)] text-sm hover:border-[var(--color-text-muted)] transition-colors"
          >
            <Upload className="w-4 h-4" />
            {t('data_restore')}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (!file) return;
              // Слияние, а не замена: у человека уже есть сегодняшние записи,
              // и импорт вчерашней копии не должен их стирать.
              void importFile(file, true);
            }}
          />

          {confirmWipe ? (
            <span className="inline-flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  flow.wipe();
                  setConfirmWipe(false);
                  notify(t('data_wiped'));
                }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[var(--color-danger-500)] text-[var(--color-danger-400)] text-sm transition-colors"
              >
                <Check className="w-4 h-4" />
                {t('data_wipe_confirm')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setConfirmWipe(false);
                }}
                className="px-4 py-2 rounded-lg border border-[var(--color-border)] text-sm transition-colors"
              >
                {t('data_cancel')}
              </button>
            </span>
          ) : (
            <button
              type="button"
              onClick={() => {
                setConfirmWipe(true);
              }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[var(--color-border)] text-sm text-[var(--color-text-muted)] hover:text-[var(--color-danger-400)] hover:border-[var(--color-danger-500)] transition-colors ml-auto"
            >
              <Trash2 className="w-4 h-4" />
              {t('data_wipe')}
            </button>
          )}
        </div>
      </Panel>
    </div>
  );
}

function Panel({
  title,
  children,
  className = '',
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section
      className={`p-5 rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-card)] ${className}`}
    >
      <h3 className="text-sm font-semibold mb-3">{title}</h3>
      {children}
    </section>
  );
}

function Row({
  label,
  unit,
  children,
}: {
  label: string;
  unit?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-[var(--color-border)] last:border-0">
      <span className="flex-1 text-sm">{label}</span>
      {children}
      {unit && <span className="text-xs text-[var(--color-text-muted)] w-16">{unit}</span>}
    </div>
  );
}
